import { useEffect, useRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { ESTADO_ETIQUETA, ESTADO_TONO } from '../lib/formato';
import type { Estado } from '../lib/tipos';

/* ───────────────────────────── Botón ─────────────────────────────────────── */

type VarianteBoton = 'principal' | 'secundario' | 'clave' | 'riesgo' | 'plano';

export function Boton({
  variante = 'principal',
  tam,
  cargando,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variante?: VarianteBoton;
  tam?: 's';
  cargando?: boolean;
}) {
  return (
    <button
      type="button"
      className="boton"
      data-variante={variante}
      data-tam={tam}
      disabled={props.disabled || cargando}
      {...props}
    >
      {cargando && <span className="girando" aria-hidden="true" />}
      {(props['aria-pressed'] === true || props['aria-pressed'] === 'true') && (
        <span className="boton-seleccion" aria-hidden="true">✓</span>
      )}
      {children}
    </button>
  );
}

/* ───────────────────────────── Superficies ───────────────────────────────── */

export function Tarjeta({
  titulo,
  extra,
  children,
  sinRelleno,
}: {
  titulo?: ReactNode;
  extra?: ReactNode;
  children: ReactNode;
  sinRelleno?: boolean;
}) {
  return (
    <section className="tarjeta">
      {titulo && (
        <header className="tarjeta-cabecera">
          <h2>{titulo}</h2>
          {extra}
        </header>
      )}
      <div className={sinRelleno ? '' : 'tarjeta-cuerpo'}>{children}</div>
    </section>
  );
}

/* ───────────────────────────── Estado ────────────────────────────────────── */

export function Pildora({ estado }: { estado: Estado }) {
  return (
    <span className="pildora" data-estado={estado} data-tono={ESTADO_TONO[estado]}>
      {ESTADO_ETIQUETA[estado]}
    </span>
  );
}

export function PildoraLibre({
  texto,
  tono = 'neutro',
}: {
  texto: string;
  tono?: 'neutro' | 'espera' | 'hecho' | 'alto';
}) {
  return (
    <span className="pildora" data-tono={tono}>
      {texto}
    </span>
  );
}

/* ───────────────────────────── Mensajes ──────────────────────────────────── */

export function Aviso({
  tono = 'info',
  children,
}: {
  tono?: 'info' | 'aviso' | 'error' | 'hecho';
  children: ReactNode;
}) {
  return (
    <div className="aviso" data-tono={tono} role={tono === 'error' ? 'alert' : undefined}>
      <div>{children}</div>
    </div>
  );
}

export function Vacio({
  titulo,
  children,
  accion,
}: {
  titulo: string;
  children?: ReactNode;
  accion?: ReactNode;
}) {
  return (
    <div className="vacio">
      <h3>{titulo}</h3>
      {children && <p>{children}</p>}
      {accion && <div style={{ marginTop: 16 }}>{accion}</div>}
    </div>
  );
}

export function Cargando({ texto = 'Cargando…' }: { texto?: string }) {
  return (
    <div className="cargando">
      <span className="girando" aria-hidden="true" />
      {texto}
    </div>
  );
}

/* ───────────────────────────── Formulario ────────────────────────────────── */

export function Campo({
  etiqueta,
  ayuda,
  error,
  children,
}: {
  etiqueta: string;
  ayuda?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="campo">
      <label>{etiqueta}</label>
      {children}
      {error ? <span className="error-texto">{error}</span> : ayuda && <span className="ayuda">{ayuda}</span>}
    </div>
  );
}

export function Lectura({ etiqueta, valor }: { etiqueta: string; valor: ReactNode }) {
  return (
    <div className="campo">
      <label>{etiqueta}</label>
      <div className="solo-lectura">{valor || <span style={{ opacity: 0.55 }}>Sin indicar</span>}</div>
    </div>
  );
}

/* ───────────────────────────── Diálogo ───────────────────────────────────── */

export function Dialogo({
  titulo,
  descripcion,
  children,
  acciones,
  onCerrar,
}: {
  titulo: string;
  descripcion?: string;
  children?: ReactNode;
  acciones: ReactNode;
  onCerrar: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const onCerrarRef = useRef(onCerrar);
  onCerrarRef.current = onCerrar;

  useEffect(() => {
    const anterior = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('input, textarea, button')?.focus();
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrarRef.current();
    };
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('keydown', esc);
      anterior?.focus();
    };
  }, []);

  return (
    <div className="velo" onMouseDown={(e) => e.target === e.currentTarget && onCerrar()}>
      <div className="dialogo" role="dialog" aria-modal="true" aria-label={titulo} ref={ref}>
        <header>
          <h2>{titulo}</h2>
          {descripcion && (
            <p className="sutil" style={{ margin: '6px 0 0' }}>
              {descripcion}
            </p>
          )}
        </header>
        {children && <div className="cuerpo">{children}</div>}
        <footer>{acciones}</footer>
      </div>
    </div>
  );
}
